/**
 * Cómo se ordena en el tiempo la lista de conversaciones.
 *
 * Una fecha absoluta al lado de cada una obliga a hacer la resta mentalmente.
 * Lo que se busca al mirar esa lista es "la de esta mañana" o "aquella de la
 * semana pasada", así que se agrupa por eso.
 */

export type Grupo = "Hoy" | "Ayer" | "Esta semana" | "Este mes" | "Antes";

const DIA = 86_400_000;

/** Medianoche local del día de esa fecha. Comparar días, no instantes. */
function aDia(fecha: Date): number {
  return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()).getTime();
}

export function grupoDe(cuando: Date, ahora = new Date()): Grupo {
  const dias = Math.round((aDia(ahora) - aDia(cuando)) / DIA);

  // Una conversación con fecha futura (reloj desajustado, servidor en otra
  // zona) es de hoy: mandarla a "Antes" la escondería al final de la lista.
  if (dias <= 0) return "Hoy";
  if (dias === 1) return "Ayer";
  if (dias < 7) return "Esta semana";
  if (dias < 31) return "Este mes";
  return "Antes";
}

/** El orden en que se pintan los grupos. */
export const ORDEN_GRUPOS: Grupo[] = ["Hoy", "Ayer", "Esta semana", "Este mes", "Antes"];

/**
 * La hora si es de hoy, el día si no.
 *
 * Dentro de "Hoy" lo que distingue una conversación de otra es la hora; en
 * "Antes", el día. Poner las dos cosas siempre es ruido.
 */
export function cuandoBreve(cuando: Date, ahora = new Date()): string {
  const grupo = grupoDe(cuando, ahora);

  if (grupo === "Hoy" || grupo === "Ayer") {
    return cuando.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  }
  if (grupo === "Antes" && cuando.getFullYear() !== ahora.getFullYear()) {
    return cuando.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  }
  return cuando.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

/** Agrupa manteniendo el orden que traía la lista (más reciente primero). */
export function agrupar<T>(
  elementos: T[],
  fechaDe: (elemento: T) => string,
  ahora = new Date(),
): { grupo: Grupo; elementos: T[] }[] {
  const cajones = new Map<Grupo, T[]>();

  for (const elemento of elementos) {
    const fecha = new Date(fechaDe(elemento));
    // Una fecha ilegible no debe tumbar la lista entera: va al final.
    const grupo = Number.isNaN(fecha.getTime()) ? "Antes" : grupoDe(fecha, ahora);
    const cajon = cajones.get(grupo);
    if (cajon) cajon.push(elemento);
    else cajones.set(grupo, [elemento]);
  }

  return ORDEN_GRUPOS.filter((g) => cajones.has(g)).map((grupo) => ({
    grupo,
    elementos: cajones.get(grupo)!,
  }));
}
